import VueCookies from 'vue-cookies'
import { v4 as uuidv4 } from 'uuid'
import { MD5 } from 'crypto-js'

/**
 * 获取动态的 unisysid
 * 优先级：URL参数 > Cookie存储 > 默认值
 * @returns {string} unisysid
 */
function getUnisysid() {
    const urlParams = new URLSearchParams(window.location.search)
    const urlUnisysid = urlParams.get('uniSysId') || urlParams.get('unisysid')

    if (urlUnisysid) {
        VueCookies.set('unisysid', urlUnisysid)
        return urlUnisysid
    }

    const cookieUnisysid = VueCookies.get('unisysid')
    if (cookieUnisysid) {
        return cookieUnisysid
    }

    return '1967846127512449025'
}

/**
 * 设置 unisysid
 * @param {string} unisysid 
 */
function setUnisysid(unisysid) {
    if (unisysid) {
        VueCookies.set('unisysid', unisysid)
    }
}

/**
 * 获取请求头参数
 * @param {Object} options - 配置选项
 * @param {String} options.token - 用户token，可选
 * @returns { TOKEN, TIMESTAMP, NONESTR, SIGNATURE, VERSION, visitorId }
 */
async function getHeaderSignature({ token } = {}) {
    const TOKEN = token || VueCookies.get('token') || ''
    const TIMESTAMP = Date.now() + Number(VueCookies.get('timeDifference'))
    const NONESTR = uuidv4().replace(/-/g, '')

    const VERSION = 2
    const n = Number(TIMESTAMP % 32)
    const s1 = NONESTR.substring(n, n + 1)
    const s2 = NONESTR.substring(32 - n - 1, 32 - n)

    let visitorRes = {}
    try {
        const storedVisitorRes = VueCookies.get('visitorRes')
        if (storedVisitorRes) {
            visitorRes = typeof storedVisitorRes === 'string' 
                ? JSON.parse(storedVisitorRes) 
                : storedVisitorRes
        }
    } catch (error) {
        console.warn('解析 visitorRes 失败:', error)
    }
    const visitorId = visitorRes.visitorId || ''

    const SIGNATURE = MD5(`${s1}${NONESTR}${s2}${TIMESTAMP * VERSION}${s2}${TOKEN}${s1}${visitorId}`).toString()
    return { TOKEN, TIMESTAMP, NONESTR, SIGNATURE, VERSION, visitorId }
}

const getFileExtension = (fileName) => {
    if (!fileName) {
        return false
    }
    return fileName.split('.').pop()
}

/**
 * 格式化字节大小
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
const formatFileSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
    }
    const units = si ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
    let u = -1
    const r = 10 ** dp
    do {
        bytes /= thresh
        ++u
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)
    return bytes.toFixed(dp) + ' ' + units[u]
}

/**
 * 随机uuid
 * @param {*} len
 * @param {*} radix 基数
 * @returns
 */
const randomUUID = (len, radix) => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('')
    let uuid = [],
        i
    radix = radix || chars.length

    if (len) {
        // Compact form
        for (i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)]
    } else {
        // rfc4122, version 4 form
        let r
        // rfc4122 requires these characters
        uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'
        uuid[14] = '4'

        // Fill in random data.  At i==19 set the high bits of clock sequence as
        // per rfc4122, sec. 4.1.5
        for (i = 0; i < 36; i++) {
            if (!uuid[i]) {
                r = 0 | (Math.random() * 16)
                uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r]
            }
        }
    }
    return uuid.join('')
}

/**
 * 获取文件base64
 * @param {*} raw
 */
const getBase64 = (raw) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        let base64URL = ''
        fileReader.readAsDataURL(raw)
        fileReader.onload = (e) => {
            base64URL = e.target.result
        }
        fileReader.onerror = () => {
            reject(new Error('文件流异常'))
        }
        fileReader.onloadend = () => {
            resolve(base64URL)
        }
    })
}

/**
 * blob url转file
 * 暂时只能兼容image
 * @param {*} blob
 * @returns File
 */
const blobUrlToFile = async (blobUrl) => {
    const blob = await fetch(blobUrl).then((res) => res.blob())
    const fileName = blob.type.split('/').pop()
    const file = new File([blob], `${new Date().getTime()}.${fileName}`, { type: blob.type })
    return file
}

const formatNumber = ({ num = 0, point = 1, suffix = 'w', digits = 5 } = {}) => {
    let numStr = num.toString()
    if (numStr.length < digits) {
        // 1w以内直接返回
        return numStr
    } else if (numStr.length >= digits) {
        // 转成w
        let decimal = numStr.substring(numStr.length - 4, numStr.length - 4 + point)
        return parseInt(num / 10000) + '.' + decimal + suffix
    }
}

// 检测编辑器内容是否为空
const checkEditorVal = (val) => {
    let num = 0
    const valReg = /<p>+(\s+|$nbsp;|&nbsp;|\s+)+<\/p>|<p>(<br>)+<\/p>/g

    while (num < val.length && val != '') {
        num++
        const k = val.match(valReg)
        if (k) {
            val = val.replace(k[0], '')
        }
    }
    return val == ''
}

const getDateStr = (date) => {
    var year = date.getFullYear()
    var month = date.getMonth() + 1
    var day = date.getDate()
    month = month > 9 ? month : '0' + month
    day = day > 9 ? day : '0' + day
    return year + '-' + month + '-' + day
}

/**
 * 复制文本
 * @param {*} text 文本
 * @returns
 */
const copyText = (text) => {
    return new Promise((resolve, reject) => {
        if (document.execCommand('copy')) {
            const textarea = document.createElement('textarea')
            textarea.value = text
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            resolve({ status: 'success' })
        } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(text).then(() => {
                resolve({ status: 'success' })
            })
        } else {
            reject(new Error('复制文本失败！'))
        }
    })
}

/**
 * 复制指定元素的格式化文本到剪贴板
 * @param {Element} dom 要复制的 dom 元素
 * @returns
 */
const copyFormattedTextToClipboard = (dom) => {
    return new Promise((resolve, reject) => {
        const range = document.createRange()
        range.selectNodeContents(dom)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        if (document.execCommand('copy')) {
            document.execCommand('copy')
            selection.removeAllRanges()
            resolve({ status: 'success' })
        } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard
                .writeText(dom.textContent)
                .then(() => {
                    resolve({ status: 'success' })
                })
                .catch((error) => {
                    reject(new Error(`复制文本失败: ${error.message}`))
                })
        } else {
            reject(new Error('复制文本失败！'))
        }
    })
}

/**
 * 更新指定的下拉选择列表
 *
 * @param {Function} apiFunction - 用于获取数据的 API 函数
 * @param {Object} params - 传递给 API 函数的参数对象
 * @param {Array} selectFormList - 选择表单项的列表
 * @param {String} key - 用于查找选择表单项的键名
 * @param {Object} options - 用于自定义返回数据的键名和其他配置。
 * @param {String} options.titleKeyName - 映射为列表中 `title` 字段的键名，默认值为 'nameCn'
 * @param {String} options.valueKeyName - 映射为列表中 `value` 字段的键名，默认值为 'id'
 * @param {Boolean} options.includeAllOption - 是否在列表中添加一个 "所有" 选项，默认值为 true。
 * @param {String} options.allOptionTitle - "所有" 选项的标题，默认值为 '所有'
 * @returns {Boolean}
 */
async function fetchAndSetDataList(apiFunction, params, selectFormList, key, options = {}) {
    const { titleKeyName = 'nameCn', valueKeyName = 'id', includeAllOption = true, allOptionTitle = '所有' } = options

    try {
        const res = await apiFunction(params)
        if (res.code !== 200) return false

        let dataList = res.data.map((item) => ({
            title: item[titleKeyName],
            value: valueKeyName == 'dictOptionCode' ? +item[valueKeyName] : item[valueKeyName]
        }))

        if (includeAllOption && dataList.length > 0) {
            dataList.unshift({
                title: allOptionTitle,
                value: null
            })
        }

        const result = selectFormList.find((item) => item.key == key)
        if (!result) return false

        result.typeList = dataList
        if (dataList.length === 0) {
            result.value = null
        }

        return true
    } catch (error) {
        console.error(error)
        return false
    }
}

function getImageSize(url) {
    return new Promise((resolve) => {
        const img = new Image()
        img.src = url
        if (img.complete) {
            resolve({ width: img.width, height: img.height })
        } else {
            img.onload = () => {
                resolve({ width: img.width, height: img.height })
            }
        }
    })
}

/**
 * 计算图片宽高比例（最大公约数简化）
 * @param {number} width 图片宽度
 * @param {number} height 图片高度
 * @returns {string} 比例字符串，如 "16:9"
 */
function calculateImageRatio(width, height) {
    if (!width || !height) {
        return '1:1'
    }

    // 计算最大公约数
    const gcd = (a, b) => {
        return b === 0 ? a : gcd(b, a % b)
    }

    const divisor = gcd(width, height)
    const ratioWidth = width / divisor
    const ratioHeight = height / divisor

    // 如果比例太大，尝试进一步简化（例如 1920:1080 -> 16:9）
    if (ratioWidth > 100 || ratioHeight > 100) {
        // 尝试除以一个更大的公约数
        const simplifiedDivisor = gcd(ratioWidth, ratioHeight)
        return `${ratioWidth / simplifiedDivisor}:${ratioHeight / simplifiedDivisor}`
    }

    return `${ratioWidth}:${ratioHeight}`
}

/**
 * 压缩图片
 * @param {File|Blob} file 图片文件
 * @param {Object} options 压缩选项
 * @param {number} options.maxSize 最大文件大小（字节），默认 10MB
 * @param {number} options.quality 压缩质量（0-1），默认 0.8
 * @param {number} options.maxWidth 最大宽度，默认 4096
 * @param {number} options.maxHeight 最大高度，默认 4096
 * @returns {Promise<{file: File, width: number, height: number, ratio: string}>}
 */
function compressImage(file, options = {}) {
    return new Promise((resolve, reject) => {
        const {
            maxSize = 10 * 1024 * 1024, // 10MB
            quality = 0.8,
            maxWidth = 4096,
            maxHeight = 4096
        } = options

        // 如果文件已经小于限制，直接返回
        if (file.size <= maxSize) {
            // 获取图片尺寸和比例
            const img = new Image()
            const url = URL.createObjectURL(file)
            img.onload = () => {
                URL.revokeObjectURL(url)
                const width = img.width
                const height = img.height
                const ratio = calculateImageRatio(width, height)
                resolve({
                    file,
                    width,
                    height,
                    ratio
                })
            }
            img.onerror = () => {
                URL.revokeObjectURL(url)
                reject(new Error('图片加载失败'))
            }
            img.src = url
            return
        }

        // 需要压缩
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                // 计算压缩后的尺寸（保持宽高比）
                let width = img.width
                let height = img.height

                // 如果图片太大，先缩小尺寸
                if (width > maxWidth || height > maxHeight) {
                    const scale = Math.min(maxWidth / width, maxHeight / height)
                    width = Math.floor(width * scale)
                    height = Math.floor(height * scale)
                }

                // 创建 canvas 进行压缩
                const canvas = document.createElement('canvas')
                canvas.width = width
                canvas.height = height
                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0, width, height)

                // 尝试不同的质量值，直到文件大小符合要求
                let currentQuality = quality
                const tryCompress = () => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('图片压缩失败'))
                                return
                            }

                            // 如果文件大小符合要求，或者质量已经很低了，就返回
                            if (blob.size <= maxSize || currentQuality <= 0.1) {
                                const fileName = file.name || `compressed_${Date.now()}.jpg`
                                const compressedFile = new File([blob], fileName, {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                })

                                const ratio = calculateImageRatio(width, height)
                                resolve({
                                    file: compressedFile,
                                    width,
                                    height,
                                    ratio
                                })
                            } else {
                                // 继续降低质量
                                currentQuality -= 0.1
                                tryCompress()
                            }
                        },
                        'image/jpeg',
                        currentQuality
                    )
                }

                tryCompress()
            }
            img.onerror = () => {
                reject(new Error('图片加载失败'))
            }
            img.src = e.target.result
        }
        reader.onerror = () => {
            reject(new Error('文件读取失败'))
        }
        reader.readAsDataURL(file)
    })
}

/**
 * 统一认证登录
 * @param {Boolean} hasDialog 是否需要询问弹窗，默认false
 */
function toUniLogin({ hasDialog = false } = {}) {
    const UniLogin_URL = window.env.UniLogin_URL
    const currentUrl = window.location.protocol + '//' + window.location.host
    const currentPath = window.location.pathname + window.location.search + window.location.hash

    // 设置回调地址为uni_web_login页面  
    const service = window.btoa(window.location.protocol + '//' + window.location.host)
    const href = `${UniLogin_URL}/user_web_login?service=${service}&currentPath=${window.btoa(currentPath)}&productType=aiWeb&time=${Date.parse(new Date())}`

    if (!hasDialog) {
        window.location.href = href
        return
    }

    // 如果需要弹窗确认，可以在这里添加确认逻辑
    // 这里暂时直接跳转
    window.location.href = href
}

/**
 * 退出登录
 * @param {Boolean} hasDialog 是否需要询问弹窗，默认false
 */
function toUniLogout({ hasDialog = false } = {}) {
    const UniLogin_URL = window.env.UniLogin_URL
    const currentUrl = window.location.protocol + '//' + window.location.host
    const currentPath = window.location.pathname + window.location.search + window.location.hash

    // 设置回调地址为uni_web_login页面，使用路由参数传递logout状态
    const service = window.btoa(window.location.protocol + '//' + window.location.host)
    const href = `${UniLogin_URL}/user_web_login/out?service=${service}&currentPath=${window.btoa(currentPath)}&productType=aiWeb&time=${Date.parse(new Date())}`

    if (!hasDialog) {
        window.location.href = href
        return
    }

    // 如果需要弹窗确认，可以在这里添加确认逻辑
    // 这里暂时直接跳转
    window.location.href = href
}

export {
    getUnisysid,
    setUnisysid,
    getHeaderSignature,
    getFileExtension,
    formatFileSize,
    randomUUID,
    getBase64,
    blobUrlToFile,
    formatNumber,
    checkEditorVal,
    getDateStr,
    copyText,
    copyFormattedTextToClipboard,
    fetchAndSetDataList,
    getImageSize,
    calculateImageRatio,
    compressImage,
    toUniLogin,
    toUniLogout
}
